import {
  FACADE_TYPES, NOT_APPLICABLE, SELECT_CONSTRUCTION, SELECT_INSTALLATION,
} from './constants';
import { localizedNumber, roundMoney } from './pricing';


export const constructionPartsFromDescription = description => String(description || '')
  .replace(/^\s*(?:Poz|Pos)\.\s*\S+\s*/i, '')
  .replace(/\s*\(B\s*=\s*[\d\s]+,\s*H\s*=\s*[\d\s]+\)\s*$/i, '')
  .split(/\s*;\s*/)
  .map(part => part.replace(/^[A-Z][A-Z0-9./_-]*\s*-\s*/, '').trim().toLocaleLowerCase('pl-PL'))
  .filter(Boolean);

export const joinConstructionParts = parts => parts.length === 2
  ? parts.join(' oraz ')
  : `${parts.slice(0, -1).join(', ')} oraz ${parts[parts.length - 1]}`;

export const calculateMontagePreview = (analysis, region, requestedSelections) => {
  const config = analysis?.configurations?.[region];
  if (!config) return null;
  const selections = Object.fromEntries(
    Object.entries(requestedSelections).filter(([, selection]) => selection.deleted),
  );
  const rows = analysis.rows.map(row => {
    const requested = requestedSelections[row.row_id] || requestedSelections[row.position] || {};
    const requestedConstruction = requested.construction_type ?? row.construction_type;
    const requiresConstruction = requestedConstruction === SELECT_CONSTRUCTION;
    const construction = requiresConstruction
      ? SELECT_CONSTRUCTION
      : (config.constructions.includes(requestedConstruction)
        ? requestedConstruction
        : (config.constructions.includes(row.construction_type)
          ? row.construction_type : SELECT_CONSTRUCTION));
    const requestedInstallation = requested.installation_type ?? row.installation_type;
    const facade = FACADE_TYPES.has(construction);
    const requiresInstallation = !facade && (
      requestedInstallation === SELECT_INSTALLATION || requestedInstallation === NOT_APPLICABLE
    );
    const installation = facade
      ? NOT_APPLICABLE
      : (requiresInstallation
        ? SELECT_INSTALLATION
        : (config.installations.includes(requestedInstallation)
          ? requestedInstallation : config.installations[0]));
    const widthValue = requested.width_m ?? row.width_m;
    const heightValue = requested.height_m ?? row.height_m;
    const quantity = Number(requested.quantity ?? row.quantity ?? 1);
    const width = Number(String(widthValue).replace(',', '.'));
    const height = Number(String(heightValue).replace(',', '.'));
    const area = width * height;
    const perimeter = 2 * (width + height);
    const coefficientRanges = construction === 'SLIDE'
      ? (config.slide_coefficients || config.coefficients)
      : config.coefficients;
    const coefficientEntry = facade ? null : coefficientRanges.find(
      entry => entry.max === null || area <= Number(entry.max),
    );
    const coefficient = facade ? 1 : 1 + Number(coefficientEntry?.value);
    const configuredPrice = config.prices[`${installation}\u0000${construction}`];
    const manualPrice = requested.unit_price;
    const rawUnitPrice = requiresInstallation || construction === SELECT_CONSTRUCTION
      ? 0 : (configuredPrice === undefined ? Number(String(manualPrice ?? '').replace(',', '.')) : Number(configuredPrice));
    const unitPrice = roundMoney(rawUnitPrice);
    const method = config.calculation_rules[construction];
    const validMethod = ['area', 'perimeter', 'perimeter_with_coefficient'].includes(method);
    let installationUnitPrice = 0;
    if (!requiresInstallation && construction !== SELECT_CONSTRUCTION && Number.isFinite(unitPrice) && validMethod) {
      if (method === 'area') installationUnitPrice = unitPrice * area;
      else if (method === 'perimeter') installationUnitPrice = unitPrice * perimeter;
      else installationUnitPrice = unitPrice * perimeter * coefficient;
      installationUnitPrice = roundMoney(installationUnitPrice);
    }
    const calculationError = (
      requiresInstallation
      ||
      construction === SELECT_CONSTRUCTION
      || !Number.isFinite(rawUnitPrice)
      || !Number.isFinite(installationUnitPrice)
      || (construction !== SELECT_CONSTRUCTION && !validMethod)
    );
    selections[row.row_id] = {
      installation_type: installation,
      construction_type: construction,
      width_m: String(widthValue),
      height_m: String(heightValue),
      quantity: String(quantity),
      ...(configuredPrice === undefined && manualPrice !== undefined ? { unit_price: String(manualPrice) } : {}),
      ...(row.added ? {
        added: true, position: row.position, source_row_id: row.source_row_id,
      } : {}),
    };
    return {
      ...row,
      width_m: String(widthValue),
      height_m: String(heightValue),
      quantity,
      installation_type: installation,
      construction_type: construction,
      unit_price: String(unitPrice),
      coefficient: String(coefficient),
      installation_unit_price: String(installationUnitPrice),
      installation_total: String(roundMoney(installationUnitPrice * quantity)),
      area_m2: String(area),
      perimeter_m: String(perimeter),
      area_total_m2: String(area * quantity),
      perimeter_total_m: String(perimeter * quantity),
      calculation_error: calculationError,
      requires_installation_selection: requiresInstallation,
      requires_construction_selection: construction === SELECT_CONSTRUCTION,
    };
  });
  const total = roundMoney(rows.reduce((sum, row) => sum + Number(row.installation_total || 0), 0));
  return { ...analysis, region, ...config, rows, total: String(total), selections };
};

/** Wiersze usług dodatkowych (demontaż / utylizacja) dopisywane do wyceny. */
export const computeMontageServices = ({
  montageAnalysis, montageRegion, montageDemolition, montageDemolitionDisposal,
}) => {
  const prices = montageAnalysis?.configurations?.[montageRegion]?.service_prices || {};
  const result = { rows: [], demolition: 0, disposal: 0, unavailable: false };
  if (!montageDemolition && !montageDemolitionDisposal) return result;
  const positionCounts = new Map();
  (montageAnalysis?.rows || []).forEach(row => {
    const occurrence = positionCounts.get(row.position) || 0;
    if (!row.display_position) positionCounts.set(row.position, occurrence + 1);
    const positionLabel = row.display_position || `${row.position}${'*'.repeat(occurrence)}`;
    if (['DEMONTAŻ', 'DEMONTAŻ_FASADY', 'UTYLIZACJA', 'UTYLIZACJA_FASADY'].includes(row.construction_type)) return;
    const facade = FACADE_TYPES.has(row.construction_type);
    const measure = Number(facade ? row.area_m2 : row.perimeter_m);
    const names = [facade ? 'DEMONTAŻ_FASADY' : 'DEMONTAŻ'];
    if (montageDemolitionDisposal) names.push(facade ? 'UTYLIZACJA_FASADY' : 'UTYLIZACJA');
    names.forEach(name => {
      const price = prices[name];
      if (price === undefined) { result.unavailable = true; return; }
      const installationUnitPrice = roundMoney(
        Number(price) * measure * (facade ? 1 : Number(row.coefficient)),
      );
      const installationTotal = roundMoney(installationUnitPrice * Number(row.quantity));
      result.rows.push({
        ...row,
        row_id: `service:${name}:${row.row_id}`,
        source_row_id: row.row_id,
        display_position: `${positionLabel}${name.startsWith('DEMONTAŻ') ? 'D' : 'U'}`,
        automatic_service: true,
        installation_type: NOT_APPLICABLE,
        construction_type: name,
        unit_price: String(price),
        installation_unit_price: String(installationUnitPrice),
        installation_total: String(installationTotal),
        service_name: name,
      });
      if (name.startsWith('DEMONTAŻ')) result.demolition += installationTotal;
      else result.disposal += installationTotal;
    });
  });
  result.demolition = roundMoney(result.demolition);
  result.disposal = roundMoney(result.disposal);
  return result;
};

/** Wiersze analizy wraz z automatycznymi wierszami usług pod odpowiednimi pozycjami. */
export const computeMontageDisplayRows = ({ montageAnalysis, montageServices }) => {
  const servicesBySource = new Map();
  montageServices.rows.forEach(row => {
    const services = servicesBySource.get(row.source_row_id) || [];
    services.push(row);
    servicesBySource.set(row.source_row_id, services);
  });
  return (montageAnalysis?.rows || []).flatMap(row => [
    row, ...(servicesBySource.get(row.row_id) || []),
  ]);
};

export const computeMontageTransportRow = ({
  montageTransport, montageTransportType, montageTransportPrice,
  montageTransportKmNumber, montageTransportKmValid,
}) => {
  if (!montageTransport || !montageTransportType || montageTransportPrice === undefined || !montageTransportKmValid) return null;
  const total = roundMoney(Number(montageTransportPrice) * montageTransportKmNumber);
  return {
    row_id: 'transport',
    quantity: 1,
    installation_unit_price: String(total),
    installation_total: String(total),
    unit_price: String(montageTransportPrice),
    service_name: 'TRANSPORT',
    non_discountable: true,
  };
};

export const computeMontagePajaczekRow = ({
  montagePajaczek, montageTransportRow, montagePajaczekDaysValid, montagePajaczekDayRate,
  montagePajaczekKmRate, montagePajaczekDaysNumber, montageTransportKmNumber,
}) => {
  if (!montagePajaczek || !montageTransportRow || !montagePajaczekDaysValid
    || montagePajaczekDayRate === undefined || montagePajaczekKmRate === undefined) return null;
  const total = roundMoney(
    montagePajaczekDaysNumber * Number(montagePajaczekDayRate)
    + montageTransportKmNumber * Number(montagePajaczekKmRate),
  );
  return {
    row_id: 'pajaczek',
    quantity: 1,
    installation_unit_price: String(total),
    installation_total: String(total),
    service_name: 'PAJACZEK',
    non_discountable: true,
  };
};

/** Sumy przed i po rabacie (rabat ogólny albo docelowa kwota brutto). */
export const computeMontageFinancials = ({
  montageDisplayRows, montageTransportRow, montagePajaczekRow, montageDiscountMode,
  montageGeneralDiscount, montageTargetGross, montageVat,
}) => {
  const rows = [
    ...montageDisplayRows,
    ...(montageTransportRow ? [montageTransportRow] : []),
    ...(montagePajaczekRow ? [montagePajaczekRow] : []),
  ];
  const originalNet = roundMoney(rows.reduce(
    (sum, row) => sum + Number(row.installation_total || 0), 0,
  ));
  const fixedNet = roundMoney(
    Number(montageTransportRow?.installation_total || 0)
    + Number(montagePajaczekRow?.installation_total || 0),
  );
  const discountableNet = roundMoney(originalNet - fixedNet);
  const vatRate = localizedNumber(montageVat);
  const originalVat = roundMoney(originalNet * vatRate / 100);
  const originalGross = roundMoney(originalNet + originalVat);
  let discount = montageDiscountMode === 'general_discount'
    ? localizedNumber(montageGeneralDiscount) || 0
    : 0;
  let targetNet = null;
  let targetBelowFixedServices = false;
  const requestedGross = localizedNumber(montageTargetGross);
  if (montageDiscountMode === 'target_gross' && requestedGross > 0) {
    const targetGross = roundMoney(requestedGross);
    const targetVat = roundMoney(targetGross * vatRate / (100 + vatRate));
    targetNet = roundMoney(targetGross - targetVat);
    targetBelowFixedServices = targetNet < fixedNet;
    if (targetBelowFixedServices) {
      targetNet = null;
      discount = 100;
    } else {
      discount = discountableNet > 0
        ? (1 - (targetNet - fixedNet) / discountableNet) * 100 : 0;
    }
  }
  const discountedRows = rows.map(row => {
    if (!discount || row.non_discountable) return {
      ...row,
      discountedUnit: Number(row.installation_unit_price),
      discountedTotal: Number(row.installation_total),
    };
    const unit = roundMoney(Number(row.installation_unit_price) * (1 - discount / 100));
    return {
      ...row,
      discountedUnit: unit,
      discountedTotal: roundMoney(unit * Number(row.quantity)),
    };
  });
  if (targetNet !== null && discountedRows.length) {
    const calculated = roundMoney(discountedRows.reduce((sum, row) => sum + row.discountedTotal, 0));
    const residual = roundMoney(targetNet - calculated);
    const residualCents = Math.round(residual * 100);
    const anchor = discountedRows.find(row => !row.non_discountable && Number(row.quantity) === 1)
      || discountedRows.find(row => (
        !row.non_discountable
        && Number(row.quantity) > 0 && residualCents % Number(row.quantity) === 0
      ));
    if (residual && anchor) {
      anchor.discountedUnit = roundMoney(anchor.discountedUnit + residual / Number(anchor.quantity));
      anchor.discountedTotal = roundMoney(anchor.discountedUnit * Number(anchor.quantity));
    }
  }
  const net = roundMoney(discountedRows.reduce((sum, row) => sum + row.discountedTotal, 0));
  const vat = targetNet !== null && requestedGross > 0
    ? roundMoney(requestedGross - net) : roundMoney(net * vatRate / 100);
  return {
    originalNet, originalVat, originalGross, net, vat, targetBelowFixedServices,
    fixedMinimumGross: roundMoney(fixedNet + roundMoney(fixedNet * vatRate / 100)),
    gross: roundMoney(net + vat), discount,
    rowsById: Object.fromEntries(discountedRows.map(row => [row.row_id, row])),
    discountedDemolition: roundMoney(discountedRows.filter(row => row.service_name?.startsWith('DEMONTAŻ')).reduce((sum, row) => sum + row.discountedTotal, 0)),
    discountedDisposal: roundMoney(discountedRows.filter(row => row.service_name?.startsWith('UTYLIZACJA')).reduce((sum, row) => sum + row.discountedTotal, 0)),
    discountedTransport: roundMoney(discountedRows.find(row => row.service_name === 'TRANSPORT')?.discountedTotal || 0),
    discountedPajaczek: roundMoney(discountedRows.find(row => row.service_name === 'PAJACZEK')?.discountedTotal || 0),
  };
};
